import { useEffect, useMemo, useRef, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { AxisBottom } from "@visx/axis";
import { scaleLinear, scaleTime } from "@visx/scale";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { bisector } from "d3-array";
import { stack as d3stack, stackOffsetWiggle, area as d3area, curveBasis } from "d3-shape";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchAutomationRateSeries } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { AutomationRatePoint } from "@/types/analytics";

type TierKey = "green" | "amber" | "red" | "unknown" | "manual";
const KEYS: TierKey[] = ["green", "amber", "red", "unknown", "manual"];
const KEY_LABELS: Record<TierKey, string> = {
  green: "Known (Green)",
  amber: "ML suggested (Amber)",
  red: "LLM fallback (Red)",
  unknown: "Unresolved",
  manual: "Manual intervention",
};
const KEY_COLOURS: Record<TierKey, string> = {
  green: "var(--color-brand-primary)",
  amber: "#EAB308",
  red: "#DC2626",
  unknown: "#888780",
  manual: "#378ADD",
};

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

export function AutomationRateStream() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<AutomationRatePoint[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchAutomationRateSeries(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  return (
    <ChartCard
      title="Automation rate over time"
      subtitle="Columns mapped per week, split by mapping tier"
      minHeight={380}
      headerBelow={
        <div className="flex flex-wrap gap-3">
          {KEYS.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]"
            >
              <span
                className="w-2.5 h-2.5 rounded-[2px]"
                style={{ background: KEY_COLOURS[k] }}
                aria-hidden="true"
              />
              {KEY_LABELS[k]}
            </span>
          ))}
        </div>
      }
    >
      {!data ? (
        <Skeleton height="h-full" className="min-h-[200px]" />
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-[12px] text-[var(--color-text-tertiary)]">
          No activity in the selected date range.
        </div>
      ) : (
        <div className="h-full w-full">
          <ParentSize>
            {({ width, height }) =>
              width > 0 && height > 0 ? (
                <StreamChart data={data} width={width} height={height} />
              ) : null
            }
          </ParentSize>
        </div>
      )}
    </ChartCard>
  );
}

interface Parsed extends AutomationRatePoint {
  dateTs: number;
}

function StreamChart({
  data,
  width,
  height,
}: {
  data: AutomationRatePoint[];
  width: number;
  height: number;
}) {
  const margin = { top: 8, right: 8, bottom: 28, left: 10 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const parsed = useMemo<Parsed[]>(
    () =>
      data.map((d) => ({
        ...d,
        dateTs: new Date(d.date).getTime(),
      })),
    [data]
  );

  const xScale = useMemo(() => {
    const ts = parsed.map((p) => p.dateTs);
    return scaleTime<number>({ range: [0, innerW], domain: [Math.min(...ts), Math.max(...ts)] });
  }, [parsed, innerW]);

  // yScale is derived from the FINAL stacked bounds. Animation interpolates
  // input values only; the scale stays fixed so layers grow within a stable
  // coordinate system rather than the axes jumping mid-animation.
  const yScale = useMemo(() => {
    const finalSeries = d3stack<Parsed, TierKey>()
      .keys(KEYS)
      .value((d, key) => d[key] ?? 0)
      .offset(stackOffsetWiggle)(parsed);
    let minY = 0;
    let maxY = 0;
    for (const layer of finalSeries) {
      for (const [y0, y1] of layer) {
        if (y0 < minY) minY = y0;
        if (y1 > maxY) maxY = y1;
      }
    }
    return scaleLinear<number>({ range: [innerH, 0], domain: [minY, maxY] });
  }, [parsed, innerH]);

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<{ point: Parsed; total: number }>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });
  const bisectTs = useMemo(() => bisector<Parsed, number>((d) => d.dateTs).center, []);

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          <AnimatedStreamLayers parsed={parsed} xScale={xScale} yScale={yScale} />

          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={(e) => {
              const pt = (
                e.currentTarget.ownerSVGElement as SVGSVGElement | null
              )?.getBoundingClientRect();
              if (!pt) return;
              const localX = e.clientX - pt.left - margin.left;
              const ts = xScale.invert(localX).valueOf();
              const idx = bisectTs(parsed, ts);
              const point = parsed[idx];
              if (!point) return;
              const total = point.green + point.amber + point.red + point.unknown + point.manual;
              showTooltip({
                tooltipData: { point, total },
                tooltipLeft: e.clientX - pt.left,
                tooltipTop: e.clientY - pt.top,
              });
            }}
            onMouseLeave={hideTooltip}
          />

          <AxisBottom
            top={innerH}
            scale={xScale}
            numTicks={Math.min(6, Math.floor(innerW / 80))}
            hideAxisLine
            tickStroke="transparent"
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "middle",
              fontFamily: "var(--font-sans)",
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">
            {new Date(tooltipData.point.dateTs).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <ul className="mt-1 text-[11px] text-[var(--color-text-secondary)]">
            {KEYS.map((k) => {
              const v = tooltipData.point[k];
              const pct = tooltipData.total > 0 ? (v / tooltipData.total) * 100 : 0;
              return (
                <li key={k} className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-[1px]"
                    style={{ background: KEY_COLOURS[k] }}
                    aria-hidden="true"
                  />
                  <span className="flex-1">{KEY_LABELS[k]}</span>
                  <span style={{ fontFamily: "var(--font-mono)" }}>
                    {v} ({pct.toFixed(0)}%)
                  </span>
                </li>
              );
            })}
          </ul>
        </TooltipInPortal>
      )}
    </div>
  );
}

/**
 * Streamgraph layers that expand from the wiggle centerline on mount, and
 * glide on data updates.
 *
 * Each frame:
 *   1. Interpolate each point's per-tier value from prev → target.
 *   2. Re-run d3-shape's stack(wiggle) on the interpolated data.
 *   3. For each layer, re-run d3-shape's area to get the path d.
 *   4. setAttribute on each layer's <path>.
 *
 * All zero values collapses the stacked area to a horizontal line (wiggle
 * offset keeps every layer's y0 == y1 == 0), so mount starts as a flat line
 * and expands outward — the "bars growing" equivalent for a streamgraph.
 */
function AnimatedStreamLayers({
  parsed,
  xScale,
  yScale,
  durationMs = 900,
}: {
  parsed: Parsed[];
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
  durationMs?: number;
}) {
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  // Prev per-point per-key values. Initial: all zeros (flat line at centerline).
  const prevValuesRef = useRef<Array<Record<TierKey, number>>>(
    parsed.map(() =>
      KEYS.reduce(
        (acc, k) => {
          acc[k] = 0;
          return acc;
        },
        {} as Record<TierKey, number>
      )
    )
  );

  useEffect(() => {
    // Ensure prev has matching length to current parsed.
    const prev = prevValuesRef.current.slice(0, parsed.length);
    while (prev.length < parsed.length) {
      prev.push(
        KEYS.reduce(
          (acc, k) => {
            acc[k] = 0;
            return acc;
          },
          {} as Record<TierKey, number>
        )
      );
    }
    const from = prev;

    const generateLayerPaths = (dataFrame: Parsed[]): string[] => {
      const stacked = d3stack<Parsed, TierKey>()
        .keys(KEYS)
        .value((d, k) => d[k] ?? 0)
        .offset(stackOffsetWiggle)(dataFrame);

      const areaGen = d3area<[number, number] & { data: Parsed }>()
        .x((d) => xScale((d as unknown as { data: Parsed }).data.dateTs) ?? 0)
        .y0((d) => yScale(d[0]) ?? 0)
        .y1((d) => yScale(d[1]) ?? 0)
        .curve(curveBasis);

      return stacked.map(
        (layer) => areaGen(layer as unknown as ([number, number] & { data: Parsed })[]) ?? ""
      );
    };

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      prevValuesRef.current = parsed.map((p) =>
        KEYS.reduce(
          (acc, k) => {
            acc[k] = p[k] ?? 0;
            return acc;
          },
          {} as Record<TierKey, number>
        )
      );
      const paths = generateLayerPaths(parsed);
      paths.forEach((d, i) => pathRefs.current[i]?.setAttribute("d", d));
      return;
    }

    let startTime: number | null = null;
    let rafId = 0;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = ease(t);

      const interpolated: Parsed[] = parsed.map((p, i) => {
        const fromPoint = from[i] ?? ({} as Record<TierKey, number>);
        const out: Parsed = { ...p };
        for (const k of KEYS) {
          const fromV = fromPoint[k] ?? 0;
          const toV = p[k] ?? 0;
          (out as unknown as Record<TierKey, number>)[k] = fromV + (toV - fromV) * eased;
        }
        return out;
      });

      // Snapshot for next update tween.
      prevValuesRef.current = interpolated.map((p) =>
        KEYS.reduce(
          (acc, k) => {
            acc[k] = (p as unknown as Record<TierKey, number>)[k] ?? 0;
            return acc;
          },
          {} as Record<TierKey, number>
        )
      );

      const paths = generateLayerPaths(interpolated);
      paths.forEach((d, i) => pathRefs.current[i]?.setAttribute("d", d));

      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [parsed, xScale, yScale, durationMs]);

  return (
    <>
      {KEYS.map((key, i) => (
        <path
          key={key}
          ref={(el) => {
            pathRefs.current[i] = el;
          }}
          fill={KEY_COLOURS[key]}
          fillOpacity={0.92}
        />
      ))}
    </>
  );
}
